import {match, Pattern} from 'ts-pattern';

import {isNil} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {
  DrillDownAdServerRedirectRequestBody,
  DrillDownLevelName,
  DrillDownStatisticsLevel,
  IntegerRange,
  SourcingVehicleDetailResponseBody,
} from '@dms/api/core';
import {OTHER_LEVEL_VALUE} from '@dms/shared';

import {Nullish} from 'shared/web';

import {getCountryAdServerName} from '../../../../utils/getCountryAdServerName';

export type GetDrillDownAdServerRequestParams = {
  levels: DrillDownStatisticsLevel[];
  vehicle: SourcingVehicleDetailResponseBody | Nullish;
  hiddenLevels: DrillDownLevelName[];
  countryCode: string | Nullish;
  currency: string | Nullish;
};

type LevelFilter = Partial<DrillDownAdServerRedirectRequestBody>;

const MOBILE_DE_SERVER_NAME = 'mobile_de';
// mobile.de is the primary server of its home market, the country mapping offers AutoScout24 there.
const MOBILE_DE_HOME_COUNTRY_CODE = 'DEU';
const VAT_RECLAIMABLE_VALUE = 'true';
const NON_NEGATIVE_INTEGER_PATTERN = /^\d+$/;
const ALPHA_MILEAGE_PATTERN = /^(\d+)k$/;
const ALPHA_MILEAGE_MULTIPLIER = 1000;
const ALPHA_MILEAGE_RANGE_SEPARATOR = '-';
// The trailing `_` keeps model family group keys (`-MODELFAMILYGROUP_`) out.
const MODEL_FAMILY_KEY_MARKER = '-MODELFAMILY_';

const getPrimaryServerName = (countryCode: string | Nullish) =>
  countryCode === MOBILE_DE_HOME_COUNTRY_CODE
    ? MOBILE_DE_SERVER_NAME
    : (getCountryAdServerName(countryCode) ?? MOBILE_DE_SERVER_NAME);

const toExactRange = (value: string): IntegerRange | null =>
  NON_NEGATIVE_INTEGER_PATTERN.test(value) ? {gte: Number(value), lte: Number(value)} : null;

const parseAlphaMileage = (text: string | undefined) => {
  const amount = ALPHA_MILEAGE_PATTERN.exec(text ?? '')?.[1];
  return isNil(amount) ? null : Number(amount) * ALPHA_MILEAGE_MULTIPLIER;
};

/**
 * Parses the alpha mileage bucket of the drill down ("<5k", "5k-15k", ">300k") into bounds.
 * Upper bounds of the buckets are exclusive, hence `lte: upper - 1`.
 */
const parseAlphaMileageRange = (value: string): IntegerRange | null =>
  match(value)
    .returnType<IntegerRange | null>()
    .with(Pattern.string.startsWith('<'), (range) => {
      const upper = parseAlphaMileage(range.slice(1));
      return isNil(upper) ? null : {lte: upper - 1};
    })
    .with(Pattern.string.startsWith('>'), (range) => {
      const lower = parseAlphaMileage(range.slice(1));
      return isNil(lower) ? null : {gte: lower};
    })
    .with(Pattern.string.includes(ALPHA_MILEAGE_RANGE_SEPARATOR), (range) => {
      const [from, to] = range.split(ALPHA_MILEAGE_RANGE_SEPARATOR);
      const lower = parseAlphaMileage(from);
      const upper = parseAlphaMileage(to);
      return isNil(lower) || isNil(upper) ? null : {gte: lower, lte: upper - 1};
    })
    .otherwise(() => null);

const getLevelFilter = (level: DrillDownStatisticsLevel): LevelFilter =>
  match(level.name)
    .returnType<LevelFilter>()
    .with('year', () => {
      const year = toExactRange(level.value);
      return isNil(year) ? {} : {year};
    })
    .with('power', () => {
      const power = toExactRange(level.value);
      return isNil(power) ? {} : {power};
    })
    .with('mileage_range', () => {
      const mileage = parseAlphaMileageRange(level.value);
      return isNil(mileage) ? {} : {mileage};
    })
    .with('fuel_type', () => ({fuelType: [level.value]}))
    .with('transmission', () => ({transmission: [level.value]}))
    .with('drive', () => ({drive: [level.value]}))
    .with('car_style', () => ({carStyle: [level.value]}))
    .with('trim_level', () => ({trimLevel: [level.value]}))
    // Only "VAT deductible" narrows the search, "not deductible" has no ad server filter.
    .with('vat_reclaimable', () =>
      level.value === VAT_RECLAIMABLE_VALUE ? {vatReclaimable: true} : {}
    )
    // make and model are resolved separately, the model family group is implied by the model.
    .with(Pattern.union('make', 'model_family_group', 'model'), () => ({}))
    // The ad server search has no seat count filter, the row still opens the wider listing.
    .with('number_of_seats', () => ({}))
    .exhaustive();

// Neither the Other bucket nor an empty value is a catalogue key or a filter value.
const isSearchableValue = (value: string | Nullish): value is string =>
  !isNilOrEmpty(value) && value !== OTHER_LEVEL_VALUE;

/**
 * MAKE_MAZDA-MODELFAMILY_6 is a model family. Matches the marker on purpose, because make and family
 * names may contain `-` (MAKE_CAN-AM-MODELFAMILY_RYKER). shouldSkipModelLevel in @dms/shared still
 * splits on the first `-`; aligning it is a known follow-up outside this change.
 */
const isModelFamilyKey = (key: string) => key.includes(MODEL_FAMILY_KEY_MARKER);

// A model family group that is itself a model family has no model level under it.
const asModelFamily = (groupKey: string | Nullish) =>
  isSearchableValue(groupKey) && isModelFamilyKey(groupKey) ? groupKey : null;

const getPathValue = (params: GetDrillDownAdServerRequestParams, levelName: DrillDownLevelName) =>
  params.levels.find((level) => level.name === levelName)?.value;

// A level hidden in the view is fixed by the vehicle, so the vehicle value stands in for it.
const getHiddenLevelVehicleKey = (
  params: GetDrillDownAdServerRequestParams,
  levelName: DrillDownLevelName,
  vehicleKey: string | Nullish
) => (params.hiddenLevels.includes(levelName) ? vehicleKey : null);

const resolveMake = (params: GetDrillDownAdServerRequestParams) =>
  [
    getPathValue(params, 'make'),
    getHiddenLevelVehicleKey(params, 'make', params.vehicle?.sourcingVehicle.make?.key),
  ].find(isSearchableValue);

const resolveModelFamily = (params: GetDrillDownAdServerRequestParams) =>
  [
    getPathValue(params, 'model'),
    asModelFamily(getPathValue(params, 'model_family_group')),
    getHiddenLevelVehicleKey(params, 'model', params.vehicle?.sourcingVehicle.model?.key),
    asModelFamily(
      getHiddenLevelVehicleKey(
        params,
        'model_family_group',
        params.vehicle?.sourcingVehicle.modelFamilyGroup?.key
      )
    ),
  ].find(isSearchableValue);

/**
 * Builds the ad server search of a drill down row from the levels of its path.
 * Returns `null` when the row cannot be searched, i.e. its make or model family is unknown.
 */
export function getDrillDownAdServerRequest(
  params: GetDrillDownAdServerRequestParams
): DrillDownAdServerRedirectRequestBody | null {
  const make = resolveMake(params);
  const modelFamily = resolveModelFamily(params);

  // Empty and Other values are already ruled out by isSearchableValue.
  if (isNil(make) || isNil(modelFamily)) {
    return null;
  }

  const levelFilters = params.levels
    .filter((level) => isSearchableValue(level.value))
    .reduce<LevelFilter>((filters, level) => ({...filters, ...getLevelFilter(level)}), {});

  return {
    ...levelFilters,
    serverName: getPrimaryServerName(params.countryCode),
    ...(isNil(params.countryCode) ? {} : {sellerCountry: params.countryCode}),
    ...(isNil(params.currency) ? {} : {currency: params.currency}),
    make,
    modelFamily,
  };
}
